import { useMemo, useState } from "react";
import { feedItems, categories } from "@/mocks/news";

const tagStyle: Record<string, string> = {
  star: "bg-primary-100 text-primary-700",
  society: "bg-accent-100 text-accent-800",
  food: "bg-secondary-200 text-secondary-900",
  tech: "bg-accent-100 text-accent-800",
  sport: "bg-secondary-100 text-secondary-900",
};

const catName: Record<string, string> = {
  star: "娱乐八卦",
  society: "社会热点",
  food: "生活趣闻",
  tech: "科技圈",
  sport: "体娱前线",
};

export default function Feed() {
  const [active, setActive] = useState("all");
  const filters = categories;

  const items = useMemo(
    () => (active === "all" ? feedItems : feedItems.filter((i) => i.category === active)),
    [active]
  );

  const handleLike = (e: React.MouseEvent<HTMLButtonElement>) => {
    const btn = e.currentTarget;
    btn.classList.toggle("text-primary-500");
    const icon = btn.querySelector("i");
    if (icon) {
      icon.classList.toggle("ri-heart-fill");
      icon.classList.toggle("ri-heart-line");
    }
  };

  const featured = items.find((i) => i.featured);
  const rest = items.filter((i) => !i.featured);

  return (
    <section id="feed" className="scroll-mt-20 bg-background-100 py-12 md:py-16">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-primary-600">
              <i className="ri-flashlight-fill" />
              New Updates
            </span>
            <h2 className="mt-2 font-heading text-3xl font-black text-foreground-950 md:text-4xl">
              新瓜速递
            </h2>
            <p className="mt-2 max-w-lg text-sm text-foreground-700">
              今日最新爆料与热门事件，选一个分类快速浏览你感兴趣的圈内动态。
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {filters.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setActive(f.id)}
                className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-all ${
                  active === f.id
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-50 text-foreground-700 hover:bg-background-200"
                }`}
              >
                <i className={f.icon} />
                {f.name}
              </button>
            ))}
          </div>
        </div>

        {items.length === 0 ? (
          <div className="mt-10 flex flex-col items-center gap-3 rounded-2xl border border-dashed border-background-300 bg-background-50 py-16 text-foreground-600">
            <i className="ri-inbox-2-line text-4xl text-secondary-500" />
            <p>该分类暂无今日更新，换个板块看看吧</p>
          </div>
        ) : (
          <div className="mt-8 grid gap-5 lg:grid-cols-3">
            {/* 头条占整行大卡 */}
            {featured && (
              <article className="group relative col-span-1 flex flex-col overflow-hidden rounded-2xl bg-background-50 shadow-card transition-shadow hover:shadow-soft lg:col-span-2 lg:flex-row">
                <div className="relative h-56 w-full shrink-0 overflow-hidden lg:h-auto lg:w-1/2">
                  <img
                    src={featured.image}
                    alt={featured.title}
                    className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-4 top-4 rounded-full bg-primary-500 px-3 py-1 text-xs font-semibold text-background-50">
                    今日头条
                  </span>
                </div>
                <div className="flex flex-1 flex-col justify-center p-5 md:p-7">
                  <div className="flex items-center gap-2 text-xs">
                    <span className={`rounded-full px-2.5 py-1 font-medium ${tagStyle[featured.category] || tagStyle.star}`}>
                      {catName[featured.category] || "热点"}
                    </span>
                    <span className="text-foreground-600">
                      <i className="ri-time-line mr-1" />
                      {featured.time}
                    </span>
                    <span className="text-foreground-600">
                      <i className="ri-eye-line mr-1" />
                      {featured.views}
                    </span>
                  </div>
                  <h3 className="mt-3 font-heading text-xl font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 md:text-2xl">
                    {featured.title}
                  </h3>
                  <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-foreground-700">
                    {featured.summary}
                  </p>
                  <div className="mt-5 flex items-center justify-between">
                    <span className="flex items-center gap-2 text-sm font-semibold text-primary-600">
                      查看全文
                      <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" />
                    </span>
                    <button
                      type="button"
                      onClick={handleLike}
                      className="flex items-center gap-1 whitespace-nowrap rounded-full border border-background-200 px-3 py-1.5 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-500"
                      aria-label="收藏这条资讯"
                    >
                      <i className="ri-heart-line" />
                      收藏
                    </button>
                  </div>
                </div>
              </article>
            )}

            {/* 其余卡片 */}
            {rest.map((item) => (
              <article
                key={item.id}
                className="group flex flex-col overflow-hidden rounded-2xl bg-background-50 shadow-card transition-all hover:-translate-y-1 hover:shadow-soft"
              >
                <div className="relative h-44 shrink-0 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 flex items-center gap-1 rounded-full bg-background-50/90 px-2.5 py-1 text-xs font-medium text-primary-700 backdrop-blur">
                    <i className="ri-flashlight-fill text-xs" />
                    {catName[item.category] || "热点"}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-4 md:p-5">
                  <div className="flex items-center gap-2 text-xs text-foreground-600">
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line" />
                      {item.time}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line" />
                      {item.views}
                    </span>
                  </div>
                  <h3 className="mt-2 font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 md:text-lg">
                    {item.title}
                  </h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-700 line-clamp-2">
                    {item.summary}
                  </p>
                  <div className="mt-4 flex items-center justify-between">
                    <button
                      type="button"
                      onClick={handleLike}
                      className="flex items-center gap-1 whitespace-nowrap rounded-full border border-background-200 px-3 py-1.5 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-500"
                      aria-label="收藏这条资讯"
                    >
                      <i className="ri-heart-line" />
                      收藏
                    </button>
                    <span className="flex items-center gap-1 text-xs font-semibold text-primary-600">
                      阅读详情
                      <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}